import { useReducer } from 'react';
import { AmwayUserDetails, Cart, CartEntryDetails, OrderType, PromotionalItemOptions } from '../services';
import { CartEventsEnum } from '../types';
import { SubCartDetail } from '../services/models/SubCartDetail';
import { PublishEvent } from './publishEventUtil';

export type CartEntryDetailsType = CartEntryDetails[];
export enum CartTotalActionEnum {
  SET_DISPLAY = 'SET_DISPLAY',
  SET_DISPLAY_PROMO_MODAL = 'SET_DISPLAY_PROMO_MODAL',
  SET_DISPLAY_UNAVAILABLE_MODAL = 'SET_DISPLAY_UNAVAILABLE_MODAL',
  SET_DISABLE_CHECKOUT = 'SET_DISABLE_CHECKOUT',
  SET_UNAVAILABLE_ENTRIES = 'SET_UNAVAILABLE_ENTRIES',
  DEFAULT_ACTION = 'DEFAULT_ACTION',
}

type CartTotalAction = {
  type: CartTotalActionEnum;
  payload: boolean | CartEntryDetailsType[] | AmwayUserDetails[] | CartEntryDetails[];
};

type CartTotalIntialState = {
  display: boolean;
  disableCheckout: boolean;
  displayExpiryModal: boolean;
  displayPromoModal: boolean;
  displayUnavailableModal: boolean;
  expiredPromotionEntries: CartEntryDetailsType[];
  expiredPromotionForAmwayUser: AmwayUserDetails[];
  unavailableEntries: CartEntryDetails[];
};

export const showUnselectedPromoDrawer = (
  entries: CartEntryDetails[],
  promotionalItemOptions: PromotionalItemOptions[],
) => {
  const promotionIds: {
    [key: string]: boolean;
  } = {};
  const totalSelectableQuantity = promotionalItemOptions?.reduce((acc, promotion) => {
    promotionIds[promotion.prmId as string] = true;
    return acc + Number(promotion.totalSelectableQuantity);
  }, 0);

  const totalSelectedQuantity = entries?.reduce(
    (acc, entry) => (promotionIds[entry.partnerPromotionId as string] ? acc + entry.quantity : acc),
    0,
  );

  return Number(totalSelectedQuantity) < Number(totalSelectableQuantity);
};

export const disableCheckoutGCO = (data: Cart) => {
  if (data.orderType !== OrderType.Gco) {
    return false;
  }
  if (data.subCarts.length === 1 && (!data.subCarts[0]?.entries || data.subCarts[0]?.entries?.length === 0)) {
    return true;
  }
  const subCartEmptyIndex = data.subCarts?.find(
    (subCart) => subCart.entries?.length === 0 && subCart.subCartId !== data.id,
  );
  return !!subCartEmptyIndex;
};

export const getPVBV = (data: Cart, activeCart: SubCartDetail, isMainPage = false) => ({
  pointValue: isMainPage ? data.totalPrice?.amwayValue?.pointValue : activeCart.totalPrice?.amwayValue?.pointValue,
  businessVolume: isMainPage
    ? data.totalPrice?.amwayValue?.businessVolume
    : activeCart.totalPrice?.amwayValue?.businessVolume,
});

export const totalInitialState: CartTotalIntialState = {
  display: false,
  disableCheckout: false,
  displayExpiryModal: false,
  displayPromoModal: false,
  displayUnavailableModal: false,
  expiredPromotionEntries: [],
  expiredPromotionForAmwayUser: [],
  unavailableEntries: [],
};

export const totalReducer = (state: CartTotalIntialState, action: CartTotalAction) => {
  const {
    SET_DISPLAY,
    SET_DISABLE_CHECKOUT,
    SET_DISPLAY_UNAVAILABLE_MODAL,
    SET_DISPLAY_PROMO_MODAL,
    SET_UNAVAILABLE_ENTRIES,
  } = CartTotalActionEnum;
  switch (action.type) {
    case SET_DISPLAY:
      return { ...state, display: action.payload as boolean };
    case SET_DISABLE_CHECKOUT:
      return { ...state, disableCheckout: action.payload as boolean };
    case SET_DISPLAY_UNAVAILABLE_MODAL:
      return { ...state, displayUnavailableModal: action.payload as boolean };
    case SET_DISPLAY_PROMO_MODAL:
      return { ...state, displayPromoModal: action.payload as boolean };
    case SET_UNAVAILABLE_ENTRIES:
      return {
        ...state,
        unavailableEntries: action.payload as CartEntryDetails[],
      };

    default:
      return state;
  }
};

export function useTotalReducer(
  data: Cart,
  entries: CartEntryDetails[],
  promotionalItemOptions: PromotionalItemOptions[],
  clickCheckoutHandler: () => void,
) {
  const [state, dispatch] = useReducer(totalReducer, totalInitialState);
  const { SET_DISPLAY, SET_DISPLAY_PROMO_MODAL, SET_DISPLAY_UNAVAILABLE_MODAL } = CartTotalActionEnum;

  const showOverview = () => {
    if (entries?.length != 0 && (data?.subTotal?.value as number) > 0) {
      dispatch({ type: SET_DISPLAY, payload: !state.display });
    }
  };
  const handleShowDrawer = () => {
    if ((data?.subTotal?.value as number) > 0) {
      dispatch({ type: SET_DISPLAY, payload: true });
    }
  };

  const handlePromotionClick = () => {
    dispatch({ type: SET_DISPLAY_PROMO_MODAL, payload: false });
    PublishEvent({
      cartEvent: CartEventsEnum.OpenPromotionDrawer,
    });
  };
  const handleReviewCartClick = () => {
    dispatch({ type: SET_DISPLAY_PROMO_MODAL, payload: false });
  };

  const handleCheckoutClick = () => {
    if (showUnselectedPromoDrawer(entries, promotionalItemOptions)) {
      dispatch({ type: SET_DISPLAY_PROMO_MODAL, payload: true });
      return;
    }

    if (state.unavailableEntries.length > 0) {
      dispatch({ type: SET_DISPLAY_UNAVAILABLE_MODAL, payload: true });
    }

    clickCheckoutHandler();
  };

  return {
    state,
    dispatch,
    showOverview,
    handleShowDrawer,
    handlePromotionClick,
    handleCheckoutClick,
    handleReviewCartClick,
  };
}

export const getDataForCartOverview = (mainResponse: Cart, subCart: SubCartDetail, displayCheckoutBtn: boolean) =>
  displayCheckoutBtn ? mainResponse : subCart;

export const getAllUnavailableEntries = (entries?: CartEntryDetails[]) => {
  const unavailbleProducts = entries?.filter((entry) => entry?.product?.flags?.restrictFromCheckout);
  return unavailbleProducts ?? [];
};

export const displayPVBVValues = (isABO: boolean, displayPVBV: boolean, data: Cart) =>
  isABO &&
  displayPVBV &&
  Boolean(data.totalPrice?.amwayValue?.pointValue) &&
  Boolean(data.totalPrice?.amwayValue?.pointValue);
